import React, { useEffect, useMemo, useRef, useState } from "react";
import { Check } from "lucide-react";

import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import EntityConstants from "../../configs/EntityConstants";
import { GET, POST, PUT } from "../../service/CRUDService";
import Toast from "../../common/Toast/Toast";
import SelectTemplateStep from "./SelectTemplateStep";
import CustomizeTemplateStep from "./CustomizeTemplateStep";
import { pageRootTestIds } from "../../testing/pageRootTestIds";
import { EMAIL_TEMPLATE_TOOLBAR_OPTIONS } from "../EmailTemplateEditor/emailTemplateSunEditorOptions";

const TEMPLATE_PAGE_LIMIT = 100;

const toolbarOptions = EMAIL_TEMPLATE_TOOLBAR_OPTIONS;

const extractTemplateId = (data) =>
  data?.template?.template_id ||
  data?.data?.template?.template_id ||
  data?.data?.template_id ||
  data?.template_id ||
  data?.id ||
  "";

const CloneEmailTemplateBuilder = ({ onBack, onSuccess }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const sunEditorRef = useRef(null);

  const [initialSelectedTemplateId] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const val = params.get("templateId") || location.state?.selectedTemplateId || sessionStorage.getItem("clone_templateId") || "";
    if (val) sessionStorage.setItem("clone_templateId", val);
    return val;
  });
  const [fromPath] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const val = params.get("from") || location.state?.fromPath || sessionStorage.getItem("clone_fromPath") || "";
    if (val) sessionStorage.setItem("clone_fromPath", val);
    return val;
  });
  const returnState = location.state?.returnState || null;

  const [step, setStep] = useState(1);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [templates, setTemplates] = useState([]);

  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);

  const [templateName, setTemplateName] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [templateNameError, setTemplateNameError] = useState("");
  const [emailSubjectError, setEmailSubjectError] = useState("");
  const [editorContent, setEditorContent] = useState("");
  const [initialEditorContent, setInitialEditorContent] = useState("");
  const [clonedTemplateId, setClonedTemplateId] = useState("");

  const [isContinuing, setIsContinuing] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const isStepOneCompleted = step > 1;
  const isStepOneActive = step === 1;
  const isStepTwoActive = step === 2;
  const connectorProgressWidth = isStepTwoActive
    ? "100%"
    : isContinuing
      ? "50%"
      : "0%";

  const navigateBackToOrigin = (templateIdToSelect = "") => {
    const originPath = fromPath;
    sessionStorage.removeItem("clone_fromPath");
    sessionStorage.removeItem("clone_templateId");

    const selectedId =
      templateIdToSelect ||
      selectedTemplate?.template_id ||
      initialSelectedTemplateId;

    if (originPath) {      
      navigate(originPath, {
        state: {
          ...(returnState || {}),
          reopenEmailPreviewModal: true,
          createdTemplateId: selectedId,
          initialTemplateId: selectedId,
        },
      });
      return;
    }

    if (onBack) {
      onBack();
      return;
    }

    navigate(-1);
  };

  const sleep = (ms) =>
    new Promise((resolve) => {
      setTimeout(resolve, ms);
    });

  const fetchTemplateHtml = async (template, fallbackHtml = "") => {
    setPreviewLoading(true);
    const htmlUrl =
      template?.html_file_path || template?.html_url || template?.html_file;

    if (!htmlUrl) {
      if (template?.email_body) {
        setPreviewHtml(template.email_body);
        setEditorContent(template.email_body);
      } else if (fallbackHtml) {
        setPreviewHtml(fallbackHtml);
        setEditorContent(fallbackHtml);
      }
      setPreviewLoading(false);
      return;
    }

    try {
      let htmlText = "";

      for (let attempt = 0; attempt < 3; attempt += 1) {
        const htmlResponse = await fetch(htmlUrl, { cache: "no-store" });
        if (htmlResponse.ok) {
          htmlText = (await htmlResponse.text())?.trim() || "";
          if (htmlText) {
            break;
          }
        }
        await sleep(350);
      }

      if (!htmlText) {
        throw new Error("Template HTML is empty");
      }

      setPreviewHtml(htmlText);
      setEditorContent(htmlText);

      if (sunEditorRef.current && typeof sunEditorRef.current.setContents === 'function') {
        sunEditorRef.current.setContents(htmlText);
      }
    } catch (error) {
      console.error("Error fetching selected template HTML:", error);
      if (template?.email_body) {
        setPreviewHtml(template.email_body);
        setEditorContent(template.email_body);
        return;
      }
      if (fallbackHtml) {
        setPreviewHtml(fallbackHtml);
        setEditorContent(fallbackHtml);
        if (sunEditorRef.current && typeof sunEditorRef.current.setContents === 'function') {
          sunEditorRef.current.setContents(fallbackHtml);
        }
        return;
      }
      Toast({
        type: "error",
        title: t("settings:error"),
        message: t("settings:failed_fetch_email_template_content"),
      });
    } finally {
      setPreviewLoading(false);
    }
  };

  const fetchEmailTemplates = async (autoSelectActive = false) => {
    setLoadingTemplates(true);
    try {
      const response = await GET(
        `${EntityConstants.EMAIL_TEMPLATES}?limit=${TEMPLATE_PAGE_LIMIT}&offset=0`,
      );
      const data = await response.json();

      if (!response.ok) {
        Toast({
          type: "error",
          title: t("settings:error"),
          message: data.error || t("settings:error_fetching_data"),
        });
        return [];
      }

      const templateList = data?.results || data?.email_templates || [];
      setTemplates(templateList);

      if (autoSelectActive && !selectedTemplate) {
        const preferredTemplate =
          templateList.find(
            (template) => template.template_id === initialSelectedTemplateId,
          ) ||
          templateList.find((template) => template.is_active) ||
          templateList[0] ||
          null;

        if (preferredTemplate) {
          setSelectedTemplate(preferredTemplate);
          setTemplateName(`${preferredTemplate.template_name} - Copy`);
          setEmailSubject(preferredTemplate.email_subject || "");
          await fetchTemplateHtml(preferredTemplate);
        }
      }

      return templateList;
    } catch (error) {
      console.error("Error fetching email templates:", error);
      Toast({
        type: "error",
        title: t("settings:error"),
        message: t("settings:error_fetching_email_templates_clone"),
      });
      return [];
    } finally {
      setLoadingTemplates(false);
    }
  };

  const fetchAllEmailTemplates = async () => {
    const response = await GET(
      `${EntityConstants.EMAIL_TEMPLATES}?limit=${TEMPLATE_PAGE_LIMIT}&offset=0`,
    );
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data?.error || "Failed to fetch templates");
    }

    return data?.results || data?.email_templates || [];
  };

  useEffect(() => {
    fetchEmailTemplates(true);
  }, [initialSelectedTemplateId]);


  const selectTemplate = async (template) => {
    setSelectedTemplate(template);

    if (!template) {
      setTemplateName("");
      setEmailSubject("");
      setPreviewHtml("");
      setEditorContent("");
      return;
    }

    setTemplateName(`${template.template_name} - Copy`);
    setEmailSubject(template.email_subject || "");
    await fetchTemplateHtml(template);
  };

  const templateOptions = useMemo(
    () =>
      templates.map((template) => ({
        value: template.template_id,
        label: template.template_name,
        name: template.template_name,
        template,
      })),
    [templates],
  );

  const selectedOption = templateOptions.find(
    (option) => option.value === selectedTemplate?.template_id,
  );

  const handleContinue = async () => {
    const trimmedTemplateName = templateName.trim();
    const trimmedEmailSubject = emailSubject.trim();

    setTemplateNameError("");
    setEmailSubjectError("");

    if (!selectedTemplate) {
      return;
    }

    if (!trimmedTemplateName) {
      setTemplateNameError(t("settings:template_name_required"));
    }

    if (!trimmedEmailSubject) {
      setEmailSubjectError(t("settings:email_subject_required"));
    }

    if (!trimmedTemplateName || !trimmedEmailSubject) {
      return;
    }

    setIsContinuing(true);

    try {
      const cloneResponse = await POST(EntityConstants.EMAIL_TEMPLATE_CLONE, {
        template_id: selectedTemplate.template_id,
        template_name: trimmedTemplateName,
        email_subject: trimmedEmailSubject,
      });
      const cloneData = await cloneResponse.json();

      if (!cloneResponse.ok) {
        Toast({
          type: "error",
          title: cloneData.error || t("settings:error"),
          message: cloneData.message || cloneData.error || t("settings:error_cloning_email_template"),
        });
        return;
      }

      const newTemplateId = extractTemplateId(cloneData);
      if (!newTemplateId) {
        Toast({
          type: "error",
          title: t("settings:error"),
          message: t("settings:error_cloning_email_template"),
        });
        return;
      }

      const activateResponse = await PUT(EntityConstants.EMAIL_TEMPLATES, {
        template_id: newTemplateId,
        is_active: true,
      });
      const activateData = await activateResponse.json();

      if (!activateResponse.ok) {
        Toast({
          type: "error",
          title: activateData.error || t("settings:error"),
          message: activateData.message || activateData.error || t("settings:error_processing_request"),
        });
        return;
      }

      const previousHtml = editorContent || previewHtml;
      const refreshedTemplates = await fetchAllEmailTemplates();
      setTemplates(refreshedTemplates);
      const activeTemplate =
        refreshedTemplates.find(
          (template) => template.template_id === newTemplateId,
        ) ||
        refreshedTemplates.find((template) => template.is_active) ||
        null;

      if (activeTemplate) {
        setClonedTemplateId(activeTemplate.template_id || newTemplateId);
        setSelectedTemplate(activeTemplate);
        setEmailSubject(activeTemplate.email_subject || trimmedEmailSubject);
        setTemplateName(activeTemplate.template_name || trimmedTemplateName);
        await fetchTemplateHtml(activeTemplate, previousHtml);
      } else {
        setClonedTemplateId(newTemplateId);
      }

      Toast({
        type: "success",
        title: t("settings:success"),
        message:
          cloneData.message || t("settings:email_template_cloned_success"),
      });

      setStep(2);
    } catch (error) {
      console.error("Error in clone/activate flow:", error);
      Toast({
        type: "error",
        title: t("settings:error"),
        message: error.message || t("settings:error_cloning_email_template"),
      });
    } finally {
      setIsContinuing(false);
    }
  };

  const handleCreateTemplate = async () => {
    const templateId = clonedTemplateId || selectedTemplate?.template_id;

    if (!templateId) {
      Toast({
        type: "error",
        title: t("settings:error"),
        message: t("settings:failed_clone_email_template"),
      });
      return;
    }

    if (!emailSubject.trim()) {
      Toast({
        type: "error",
        title: t("settings:error"),
        message: t("settings:email_subject_required"),
      });
      return;
    }

    if (!editorContent.trim()) {
      Toast({
        type: "error",
        title: t("settings:error"),
        message: t("settings:email_body_required"),
      });
      return;
    }

    setIsCreating(true);
    try {
      const response = await POST(EntityConstants.EMAIL_TEMPLATES, {
        template_id: templateId,
        email_subject: emailSubject,
        email_body: editorContent,
      });
      const data = await response.json();

      if (!response.ok) {
        Toast({
          type: "error",
          title: t("settings:error"),
          message: data.error || data.message || t("settings:error_processing_request"),
        });
        return;
      }

      Toast({
        type: "success",
        title: t("settings:success"),
        message: data.message || t("settings:email_template_cloned_success"),
      });

      navigateBackToOrigin(templateId);

      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error("Error creating cloned template:", error);
      Toast({
        type: "error",
        title: t("settings:error"),
        message: error.message || t("settings:error_processing_request"),
      });
    } finally {
      setIsCreating(false);
    }
  };

  const handleInsertVariable = (variable) => {
    if (sunEditorRef.current) {
      sunEditorRef.current.insertHTML(`{{${variable}}}`);
    }
  };

  return (
    <div className="mx-auto bg-white min-h-screen mt-20" id={pageRootTestIds.cloneEmailTemplateBuilder}>
      <div className="w-full bg-cfSearchContainer">
        <div className="flex justify-between px-6 py-3 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div>
              <p className="self-start my-auto text-2xl font-semibold text-[#101828]">
                {t("settings:clone_template") || "Clone Template"}
              </p>
            </div>
          </div>
        </div>
      </div>
      <div className="px-6 py-6 border-b border-gray-200 bg-cfSecondary">
        <div className="max-w-6xl mx-auto flex items-start gap-10">
          <div className="flex flex-col items-center min-w-[140px]">
            <div
              className={`w-12 h-12 rounded-full flex items-center justify-center text-lg font-semibold ${
                isStepOneCompleted
                  ? "bg-cfPrimary text-cfSecondary"
                  : isStepOneActive
                    ? "bg-cfPrimary text-cfSecondary ring-4 ring-[#DBEAFE]"
                    : "bg-white border border-cfIssuedScreenSearchBorder text-cfGlobalText"
              }`}
            >
              {isStepOneCompleted ? <Check size={20} /> : 1}
            </div>
            <p
              className={`mt-2 text-base font-semibold ${
                isStepOneActive ? "text-cfPrimary" : "text-cfHeadingText"
              }`}
            >
              {t("settings:select_template")}
            </p>
            <p className="text-sm text-cfGlobalText text-center mt-1">
              {t("settings:stepper1")}
            </p>
          </div>
          <div className="flex-1 h-[2px] bg-cfIssuedScreenSearchBorder mt-6 rounded-full">
            <div
              className="h-full bg-cfPrimary transition-all duration-300 rounded-full"
              style={{ width: connectorProgressWidth }}
            />
          </div>
          <div className="flex flex-col items-center min-w-[140px]">
            <div
              className={`w-12 h-12 rounded-full flex items-center justify-center text-lg font-semibold ${
                isStepTwoActive
                  ? "bg-cfPrimary text-cfSecondary ring-4 ring-[#DBEAFE]"
                  : "bg-white border border-cfIssuedScreenSearchBorder text-cfGlobalText"
              }`}
            >
              2
            </div>
            <p
              className={`mt-2 text-base font-semibold ${
                isStepTwoActive ? "text-cfPrimary" : "text-cfHeadingText"
              }`}
            >
              {t("settings:customize")}
            </p>
            <p className="text-sm text-cfGlobalText text-center mt-1">
              {t("settings:stepper2")}
            </p>
          </div>
        </div>
      </div>
      {step === 1 ? (
        <SelectTemplateStep
          templateOptions={templateOptions}
          selectedOption={selectedOption}
          onSelectTemplate={selectTemplate}
          loadingTemplates={loadingTemplates}
          templateName={templateName}
          onTemplateNameChange={(value) => {
            setTemplateName(value);
            if (templateNameError) {
              setTemplateNameError("");
            }
          }}
          templateNameError={templateNameError}
          emailSubject={emailSubject}
          onEmailSubjectChange={(value) => {
            setEmailSubject(value);
            if (emailSubjectError) {
              setEmailSubjectError("");
            }
          }}
          emailSubjectError={emailSubjectError}
          selectedTemplate={selectedTemplate}
          onBack={() => navigateBackToOrigin()}
          onContinue={handleContinue}
          isContinuing={isContinuing}
          previewHtml={previewHtml}
          previewLoading={previewLoading}
        />
      ) : (
        <CustomizeTemplateStep
          templateName={templateName}
          onTemplateNameChange={setTemplateName}
          emailSubject={emailSubject}
          onEmailSubjectChange={setEmailSubject}
          onEditorReady={(instance) => {
            sunEditorRef.current = instance;
            if (!instance) return;

            instance.setContents(editorContent || "");
            const normalizedContent = instance.getContents() || "";
            setInitialEditorContent(normalizedContent);
            setEditorContent(normalizedContent);
          }}
          toolbarOptions={toolbarOptions}
          onEditorChange={setEditorContent}
          editorContent={editorContent}
          onInsertVariable={handleInsertVariable}
          onPrevious={() => {
            sunEditorRef.current = null;
            setInitialEditorContent("");
            setStep(1);
          }}
          onCreate={handleCreateTemplate}
          isCreating={isCreating}
          isDataChanged={editorContent !== initialEditorContent}
        />
      )}
    </div>
  );
};

export default CloneEmailTemplateBuilder;
